import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Button } from "antd";
import { useTranslation } from "react-i18next";

import { NEW_BLUE, BLUE_675, BLACK } from "../../constant/colors";
import { fontWeights } from "../../constant/styles";

const ExpandableNoteContentContainer = styled.div`
  font-weight: ${fontWeights.regular};
  color: ${BLACK};
  font-size: 14px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const SeeLessButton = styled(Button)`
  color: ${BLACK};
  font-weight: ${fontWeights.bold};
  font-size: 14px;
  padding: 0;
  margin: 0;
  height: auto;
  border: none;
  background: none;
  box-shadow: none;
  display: inline;
  margin-left: 4px;
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${BLUE_675};
    background: none;
    border: none;
    box-shadow: none;
  }

  &:active {
    color: ${NEW_BLUE};
  }
`;

const SeeMoreInlineSpan = styled.span`
  color: ${BLACK};
  font-weight: ${fontWeights.bold};
  font-size: 14px;
  cursor: pointer;
  margin-left: 4px;

  &:hover {
    color: ${BLUE_675};
  }

  &:active {
    color: ${NEW_BLUE};
  }
`;

const MeasurementDiv = styled.div`
  position: absolute;
  visibility: hidden;
  height: auto;
  width: 100%;
  font-weight: ${fontWeights.regular};
  color: ${BLACK};
  font-size: 14px;
  white-space: pre-line;
  overflow: hidden;
  pointer-events: none;
  word-break: break-word;
  overflow-wrap: break-word;
  top: 0;
  left: 0;
`;

export const ExpandableNoteContent = ({ children }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const [truncatedText, setTruncatedText] = useState("");
  const containerRef = useRef(null);
  const measurementRef = useRef(null);

  useEffect(() => {
    if (!children || !containerRef.current || !measurementRef.current) {
      setShouldTruncate(false);
      return;
    }

    const calculateTruncation = () => {
      const measurement = measurementRef.current;
      const container = containerRef.current;

      if (!measurement || !container) {
        return;
      }

      const seeMoreText = t("patient.notes.see_more");
      const suffix = `... ${seeMoreText}`;

      // Set up measurement div with exact same styles
      const containerStyles = getComputedStyle(container);
      measurement.style.width = containerStyles.width;
      measurement.style.fontSize = containerStyles.fontSize;
      measurement.style.fontFamily = containerStyles.fontFamily;
      measurement.style.fontWeight = containerStyles.fontWeight;
      measurement.style.lineHeight = containerStyles.lineHeight;

      // First, check if truncation is needed at all
      measurement.textContent = children;
      const lineHeight = parseFloat(getComputedStyle(measurement).lineHeight);
      const maxHeight = lineHeight * 3;

      if (measurement.scrollHeight <= maxHeight) {
        setShouldTruncate(false);
        return;
      }

      setShouldTruncate(true);

      if (isExpanded) return;

      // Find the maximum text that fits in exactly 3 lines with suffix
      let left = 0;
      let right = children.length;
      let bestLength = 0;

      // Binary search for the longest text that fits
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const testText = children.substring(0, mid);
        const fullText = testText + suffix;

        measurement.textContent = fullText;
        const currentHeight = measurement.scrollHeight;

        if (currentHeight <= maxHeight) {
          // Check if this actually creates exactly 3 lines or less
          const lines = Math.round(currentHeight / lineHeight);
          if (lines <= 3) {
            bestLength = mid;
            left = mid + 1;
          } else {
            right = mid - 1;
          }
        } else {
          right = mid - 1;
        }
      }

      // Get the best fit text
      let finalText = children.substring(0, bestLength);

      // Try to end at a word boundary if we don't lose too much
      const lastSpaceIndex = finalText.lastIndexOf(" ");
      if (lastSpaceIndex > finalText.length * 0.7) {
        finalText = finalText.substring(0, lastSpaceIndex);
      }

      // Final validation: ensure it still fits with the suffix
      measurement.textContent = finalText + suffix;
      while (measurement.scrollHeight > maxHeight && finalText.length > 0) {
        // Remove one character at a time until it fits
        finalText = finalText.substring(0, finalText.length - 1);

        // Try to maintain word boundary
        if (finalText.endsWith(" ")) {
          finalText = finalText.trim();
        }

        measurement.textContent = finalText + suffix;
      }

      setTruncatedText(finalText.trim());
    };

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current && measurementRef.current) {
        requestAnimationFrame(calculateTruncation);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    requestAnimationFrame(calculateTruncation);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [children, isExpanded, t]);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!shouldTruncate || isExpanded) {
    return (
      <ExpandableNoteContentContainer ref={containerRef}>
        {children}
        {shouldTruncate && isExpanded && (
          <SeeLessButton type="text" onClick={handleToggleExpand} id="see_less_inline">
            {t("patient.notes.see_less")}
          </SeeLessButton>
        )}
        <MeasurementDiv ref={measurementRef} />
      </ExpandableNoteContentContainer>
    );
  }

  return (
    <ExpandableNoteContentContainer ref={containerRef}>
      {truncatedText}...
      <SeeMoreInlineSpan onClick={handleToggleExpand} id="see_more_inline">
        {t("patient.notes.see_more")}
      </SeeMoreInlineSpan>
      <MeasurementDiv ref={measurementRef} />
    </ExpandableNoteContentContainer>
  );
};
